/**
 * Selection target model.
 *
 * `target` describes the desired FINAL list size, plus the intermediate
 * shortlist size (round-1 output). Each is entered as either a percentage of the
 * candidate scope or an absolute number of shows. The shortlist is suggested
 * from the final (≈2×, so round 2 has room to narrow), and the per-voter
 * max-picks is suggested from the shortlist.
 *
 * Stored under selection.settings.target so it needs no schema change. All math
 * lives here so the create dialog and the detail plan editor agree.
 */

export type TargetUnit = 'pct' | 'count';
export interface TargetField {
  mode: TargetUnit;
  value: number;
}
export interface SelectionTarget {
  final: TargetField;
  shortlist: TargetField;
}

// Shortlist ≈ 2× final (gives round 2 something to narrow). Assumed voter
// overlap for the max-picks suggestion.
export const SHORTLIST_FACTOR = 2;
export const OVERLAP_FACTOR = 1.6;

/** Resolve a field to an absolute number of shows against the current scope. */
export function resolveTargetField(scopeSize: number, field: TargetField | undefined): number {
  if (!field) return 0;
  if (field.mode === 'count') return Math.max(0, Math.round(field.value || 0));
  return Math.round((scopeSize * (field.value || 0)) / 100);
}

/** Suggested shortlist COUNT given a final count. */
export function suggestShortlistCount(finalCount: number): number {
  return Math.max(finalCount, Math.round(finalCount * SHORTLIST_FACTOR));
}

/** Suggested per-voter max picks for a nominate round of this shortlist size. */
export function suggestMaxPicks(shortlistCount: number, voterCount: number): number {
  if (voterCount <= 0) return 0;
  return Math.ceil((shortlistCount * OVERLAP_FACTOR) / voterCount);
}

export function defaultTarget(): SelectionTarget {
  return { final: { mode: 'pct', value: 15 }, shortlist: { mode: 'pct', value: 30 } };
}

/** Derive a shortlist field from a final field (same unit, ~2× the value). */
export function deriveShortlist(final: TargetField): TargetField {
  if (final.mode === 'count') {
    return { mode: 'count', value: Math.max(1, Math.round(final.value * SHORTLIST_FACTOR)) };
  }
  return { mode: 'pct', value: Math.min(100, Math.round(final.value * SHORTLIST_FACTOR)) };
}

/**
 * Read the target from a selection, falling back to the legacy target_pct
 * column and then to defaults, so older selections keep working.
 */
export function getSelectionTarget(selection: {
  settings?: Record<string, unknown> | null;
  target_pct?: number | null;
}): SelectionTarget {
  const t = (selection.settings as { target?: SelectionTarget } | null | undefined)?.target;
  if (t && t.final && t.shortlist) return t;
  if (selection.target_pct != null) {
    const finalPct = Math.round(selection.target_pct * 100);
    return {
      final: { mode: 'pct', value: finalPct },
      shortlist: { mode: 'pct', value: Math.min(100, finalPct * SHORTLIST_FACTOR) },
    };
  }
  return defaultTarget();
}

/** Switch a field's unit while preserving the resolved number of shows. */
export function convertField(field: TargetField, to: TargetUnit, scopeSize: number): TargetField {
  if (field.mode === to) return field;
  const count = resolveTargetField(scopeSize, field);
  if (to === 'count') return { mode: 'count', value: count };
  return { mode: 'pct', value: scopeSize > 0 ? Math.round((count / scopeSize) * 100) : 0 };
}

/** Short human label for a field, e.g. "15%" or "10 shows". */
export function targetLabel(field: TargetField): string {
  return field.mode === 'pct' ? `${field.value}%` : `${field.value} show${field.value === 1 ? '' : 's'}`;
}
